import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, RotateCcw } from 'lucide-react';
import { useSWRConfig } from 'swr';

import { BadgeRow, formatRelativeOrShort } from './salesMapShared';

import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  toast,
  MAP_WORKBENCH_RUNTIME_FEATURE_STATE_REFRESH_EVENT,
  type MapWorkbenchRuntimeSurfaceSectionRenderArgs,
} from '@workspace/ui';

import { readRuntimeSurfaceSectionComponentId } from '@/components/mapping/runtimeSurfaceRenderers';
import {
  createSalesLocationNote,
  loadSalesLocationCollaboration,
  readSalesLocationObservationMetadata,
  updateSalesLocationFlags,
  type SalesCollaborationDetail,
  type SalesLocationCallbackStatus,
  type SalesLocationContactOutcome,
  type SalesLocationInterestStatus,
  type SalesLocationObservationMetadata,
  type SalesWorkspaceContext,
} from '@/lib/sales-collaboration';
import {
  buildSalesLocationSummaryKey,
  buildSalesLocationSummaryRequestLocation,
} from './useSalesLocationSummary';

export const SALES_MAP_COLLABORATION_COMPONENT_ID = 'sales_location_collaboration';

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const readBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }
  return null;
};

export const isSalesMapCollaborationSection = (section: MapWorkbenchRuntimeSurfaceSectionRenderArgs['section']) =>
  section.kind === 'custom' && readRuntimeSurfaceSectionComponentId(section) === SALES_MAP_COLLABORATION_COMPONENT_ID;

const formatDateInput = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return new Date(parsed).toISOString().slice(0, 10);
};

const toIsoDateValue = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? `${trimmed}T00:00:00.000Z` : null;
};

const buildAddressSnapshot = (feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature']) => {
  const properties = feature.properties ?? {};
  const snapshot: Record<string, unknown> = {};
  const fieldMap = {
    display_name: properties.display_name,
    address_line1: properties.address_line1,
    locality: properties.locality,
    region_code: properties.region_code,
    postal_code: properties.postal_code,
    country_code: properties.country_code,
  };
  for (const [key, value] of Object.entries(fieldMap)) {
    const next = readString(value);
    if (next) {
      snapshot[key] = next;
    }
  }
  return Object.keys(snapshot).length > 0 ? snapshot : undefined;
};

const buildCoordinateSnapshot = (feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature']) => {
  const coordinates = feature.geometry?.coordinates;
  if (
    feature.geometryType === 'Point' &&
    Array.isArray(coordinates) &&
    coordinates.length >= 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  ) {
    return {
      lng: coordinates[0],
      lat: coordinates[1],
    };
  }
  return undefined;
};

const resolveFeatureIdentityKey = (feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature']) => {
  const properties = feature.properties ?? {};
  return readString(properties.feature_identity_key) ?? null;
};

const resolveLocationId = (feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature']) => {
  const properties = feature.properties ?? {};
  return readString(properties.location_id) ?? readString(properties.locationId) ?? readString(properties.id);
};

const normalizeInterestStatus = (value: unknown): SalesLocationInterestStatus | '' => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'unknown':
    case 'interested':
    case 'not_interested':
      return normalized;
    default:
      return '';
  }
};

const normalizeCallbackStatus = (value: unknown): SalesLocationCallbackStatus | '' => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'none':
    case 'requested':
    case 'completed':
      return normalized;
    default:
      return '';
  }
};

const normalizeContactOutcome = (value: unknown): SalesLocationContactOutcome | '' => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'resident_contacted':
    case 'no_answer':
      return normalized;
    default:
      return '';
  }
};

const formatContactOutcomeLabel = (value?: SalesLocationContactOutcome | null) => {
  switch (value) {
    case 'resident_contacted':
      return 'Resident contacted';
    case 'no_answer':
      return 'No answer';
    default:
      return null;
  }
};

const formatInterestStatusLabel = (value?: SalesLocationInterestStatus | '' | null) => {
  switch (value) {
    case 'unknown':
      return 'Interest unknown';
    case 'interested':
      return 'Interested';
    case 'not_interested':
      return 'Not interested';
    default:
      return null;
  }
};

const formatCallbackStatusLabel = (value?: SalesLocationCallbackStatus | '' | null) => {
  switch (value) {
    case 'none':
      return 'No callback pending';
    case 'requested':
      return 'Callback requested';
    case 'completed':
      return 'Callback completed';
    default:
      return null;
  }
};

const buildObservationMetadata = (input: {
  contactOutcome: SalesLocationContactOutcome | '';
  leftNote: boolean;
  bestTimeToVisit: string;
  followUpDate: string;
}): SalesLocationObservationMetadata | undefined => {
  const metadata: SalesLocationObservationMetadata = {};
  if (input.contactOutcome) {
    metadata.contact_outcome = input.contactOutcome;
  }
  if (input.leftNote) {
    metadata.left_note = true;
  }
  const bestTimeToVisit = input.bestTimeToVisit.trim();
  if (bestTimeToVisit) {
    metadata.best_time_to_visit = bestTimeToVisit;
  }
  const followUpDate = input.followUpDate.trim();
  if (followUpDate) {
    metadata.follow_up_date = followUpDate;
  }
  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

function SalesMapCollaborationSectionBody({
  feature,
  salesContext,
}: MapWorkbenchRuntimeSurfaceSectionRenderArgs & {
  salesContext?: SalesWorkspaceContext | null;
}) {
  const { mutate: mutateCache } = useSWRConfig();
  const featureIdentityKey = useMemo(() => resolveFeatureIdentityKey(feature), [feature]);
  const locationId = useMemo(() => resolveLocationId(feature), [feature]);
  const summaryRequestLocation = useMemo(() => buildSalesLocationSummaryRequestLocation(feature), [feature]);
  const summaryKey = useMemo(
    () => buildSalesLocationSummaryKey(summaryRequestLocation, salesContext),
    [salesContext, summaryRequestLocation],
  );
  const [detail, setDetail] = useState<SalesCollaborationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [doNotContact, setDoNotContact] = useState(false);
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDueAt, setFollowUpDueAt] = useState('');
  const [interestStatus, setInterestStatus] = useState<'' | SalesLocationInterestStatus>('');
  const [callbackStatus, setCallbackStatus] = useState<'' | SalesLocationCallbackStatus>('');
  const [contactOutcome, setContactOutcome] = useState<'' | SalesLocationContactOutcome>('');
  const [leftNote, setLeftNote] = useState(false);
  const [bestTimeToVisit, setBestTimeToVisit] = useState('');
  const [observationFollowUpDate, setObservationFollowUpDate] = useState('');

  const refresh = useCallback(async () => {
    if (!featureIdentityKey) {
      setDetail(null);
      setError('Feature identity is missing for this sales location.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await loadSalesLocationCollaboration(featureIdentityKey, salesContext);
      setDetail(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collaboration notes.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [featureIdentityKey, salesContext]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const summary = detail?.summary;
    setDoNotContact(Boolean(summary?.do_not_contact));
    setFollowUpRequired(Boolean(summary?.follow_up_required));
    setFollowUpDueAt(formatDateInput(summary?.follow_up_due_at));
    setInterestStatus(normalizeInterestStatus(summary?.interest_status));
    setCallbackStatus(normalizeCallbackStatus(summary?.callback_status));
    setContactOutcome('');
    setLeftNote(false);
    setBestTimeToVisit('');
    setObservationFollowUpDate('');
    setDraft('');
  }, [detail, featureIdentityKey]);

  useEffect(() => {
    setFormOpen(false);
  }, [featureIdentityKey]);

  const summary = detail?.summary ?? {};
  const notes = detail?.notes ?? [];
  const noteCount = summary.note_count ?? notes.length;
  const lastActivityAt = summary.latest_note_at ?? summary.latest_event_at ?? null;
  const currentInterestStatus = normalizeInterestStatus(summary.interest_status);
  const currentCallbackStatus = normalizeCallbackStatus(summary.callback_status);
  const currentFollowUpDueAt = formatDateInput(summary.follow_up_due_at);
  const flagsChanged =
    doNotContact !== Boolean(summary.do_not_contact) ||
    followUpRequired !== Boolean(summary.follow_up_required) ||
    (followUpRequired ? followUpDueAt : '') !== currentFollowUpDueAt ||
    interestStatus !== currentInterestStatus ||
    callbackStatus !== currentCallbackStatus;
  const observationMetadata = buildObservationMetadata({
    contactOutcome,
    leftNote,
    bestTimeToVisit,
    followUpDate: observationFollowUpDate,
  });
  const hasObservationMetadata = Boolean(observationMetadata);
  const canSave = Boolean(featureIdentityKey) && (flagsChanged || Boolean(draft.trim()) || hasObservationMetadata);

  const handleSubmit = useCallback(async () => {
    if (!featureIdentityKey || !canSave) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (flagsChanged) {
        await updateSalesLocationFlags(
          featureIdentityKey,
          {
            location_id: locationId,
            do_not_contact: doNotContact,
            follow_up_required: followUpRequired,
            follow_up_due_at: followUpRequired ? toIsoDateValue(followUpDueAt) : null,
            interest_status: interestStatus || null,
            callback_status: callbackStatus || null,
            address_snapshot: buildAddressSnapshot(feature),
            coordinate_snapshot: buildCoordinateSnapshot(feature),
          },
          salesContext,
        );
      }

      if (draft.trim() || observationMetadata) {
        await createSalesLocationNote(
          featureIdentityKey,
          {
            location_id: locationId,
            body: draft.trim(),
            metadata: observationMetadata,
            address_snapshot: buildAddressSnapshot(feature),
            coordinate_snapshot: buildCoordinateSnapshot(feature),
          },
          salesContext,
        );
      }

      toast.success(flagsChanged && (draft.trim() || observationMetadata) ? 'Location update saved.' : flagsChanged ? 'Location state saved.' : 'Note saved.');
      setFormOpen(false);
      await refresh();
      if (summaryKey) {
        await mutateCache(summaryKey);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(MAP_WORKBENCH_RUNTIME_FEATURE_STATE_REFRESH_EVENT));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save sales note.');
    } finally {
      setSaving(false);
    }
  }, [
    bestTimeToVisit,
    callbackStatus,
    canSave,
    contactOutcome,
    doNotContact,
    draft,
    feature,
    featureIdentityKey,
    flagsChanged,
    followUpDueAt,
    followUpRequired,
    interestStatus,
    leftNote,
    locationId,
    mutateCache,
    observationFollowUpDate,
    observationMetadata,
    refresh,
    salesContext,
    summaryKey,
  ]);

  const statusBadges = [
    { key: 'notes', node: <Badge variant="muted">{noteCount === 1 ? '1 note' : `${noteCount} notes`}</Badge> },
    ...(summary.do_not_contact ? [{ key: 'dnc', node: <Badge variant="destructive">Do not contact</Badge> }] : []),
    ...(summary.follow_up_required ? [{ key: 'followup', node: <Badge variant="outline">Follow up required</Badge> }] : []),
    ...(formatInterestStatusLabel(currentInterestStatus) ? [{
      key: 'interest',
      node: <Badge variant={currentInterestStatus === 'interested' ? 'success' : 'muted'}>{formatInterestStatusLabel(currentInterestStatus)}</Badge>,
    }] : []),
    ...(formatCallbackStatusLabel(currentCallbackStatus) ? [{
      key: 'callback',
      node: <Badge variant={currentCallbackStatus === 'requested' ? 'outline' : 'muted'}>{formatCallbackStatusLabel(currentCallbackStatus)}</Badge>,
    }] : []),
    ...(lastActivityAt ? [{ key: 'activity', node: <Badge variant="outline">Last active {formatRelativeOrShort(lastActivityAt)}</Badge> }] : []),
  ];

  return (
    <section className="flex flex-col gap-4">
      {/* Status row */}
      <div className="flex items-start justify-between gap-2">
        <BadgeRow badges={statusBadges} />
        <Button
          variant="ghost"
          size="icon"
          aria-label="Refresh notes"
          className="h-6 w-6 shrink-0 text-muted-foreground"
          disabled={loading || saving}
          onClick={() => void refresh()}
        >
          <RotateCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Notes list — rendered first so it's immediately visible */}
      {loading && !detail ? (
        <div className="space-y-2">
          <Skeleton className="h-14 rounded-lg" />
          <Skeleton className="h-14 rounded-lg" />
          <Skeleton className="h-14 rounded-lg" />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes recorded yet.</p>
      ) : (
        <div className="space-y-0">
          {notes.map(note => {
            const metadata = readSalesLocationObservationMetadata(note.metadata);
            const contactOutcomeLabel = formatContactOutcomeLabel(metadata.contact_outcome);
            const hasNarrative = Boolean(note.body && note.body.trim());
            return (
              <article
                key={note.note_id ?? `${note.created_at ?? 'note'}:${note.body ?? ''}`}
                className="border-t border-border/40 py-3 first:border-t-0 first:pt-0"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-xs font-semibold text-foreground">
                    {note.author_username || note.author_email || note.author_user_id || 'Unknown author'}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{formatRelativeOrShort(note.created_at)}</div>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {contactOutcomeLabel ? <Badge variant="outline">{contactOutcomeLabel}</Badge> : null}
                  {readBoolean(metadata.left_note) ? <Badge variant="muted">Left note</Badge> : null}
                  {readString(metadata.follow_up_date) ? (
                    <Badge variant="outline">Follow up {readString(metadata.follow_up_date)}</Badge>
                  ) : null}
                </div>
                {readString(metadata.best_time_to_visit) ? (
                  <div className="mt-1.5 text-xs text-muted-foreground">
                    Best time to visit: {readString(metadata.best_time_to_visit)}
                  </div>
                ) : null}
                <div className="mt-1.5 whitespace-pre-wrap text-sm leading-5 text-foreground">
                  {hasNarrative ? note.body : 'Structured update recorded.'}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Add update form — collapsed by default */}
      <Collapsible open={formOpen} onOpenChange={setFormOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/40"
          >
            <span className="flex items-center gap-2">
              <Plus className="h-3.5 w-3.5" />
              Add update
            </span>
            {formOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-3 flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-3 rounded-lg border border-border/70 bg-background px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Location state</div>

                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`sales-dnc-${featureIdentityKey ?? 'unknown'}`} className="text-sm text-foreground">
                    Do not contact
                  </Label>
                  <Switch
                    id={`sales-dnc-${featureIdentityKey ?? 'unknown'}`}
                    checked={doNotContact}
                    onCheckedChange={setDoNotContact}
                  />
                </div>

                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`sales-followup-${featureIdentityKey ?? 'unknown'}`} className="text-sm text-foreground">
                    Follow-up required
                  </Label>
                  <Switch
                    id={`sales-followup-${featureIdentityKey ?? 'unknown'}`}
                    checked={followUpRequired}
                    onCheckedChange={setFollowUpRequired}
                  />
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-follow-up-due-${featureIdentityKey ?? 'unknown'}`}
                    className={`text-xs font-medium ${followUpRequired ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    Follow-up due
                  </Label>
                  <Input
                    id={`sales-follow-up-due-${featureIdentityKey ?? 'unknown'}`}
                    type="date"
                    value={followUpDueAt}
                    disabled={!followUpRequired}
                    onChange={event => setFollowUpDueAt(event.target.value)}
                    className="h-8 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-interest-status-${featureIdentityKey ?? 'unknown'}`}
                    className="text-xs font-medium text-foreground"
                  >
                    Interest status
                  </Label>
                  <Select
                    value={interestStatus || '__unset__'}
                    onValueChange={v => setInterestStatus(normalizeInterestStatus(v === '__unset__' ? '' : v))}
                  >
                    <SelectTrigger id={`sales-interest-status-${featureIdentityKey ?? 'unknown'}`} className="h-8 text-sm" />
                    <SelectContent>
                      <SelectItem value="__unset__">Not set</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="not_interested">Not interested</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-callback-status-${featureIdentityKey ?? 'unknown'}`}
                    className="text-xs font-medium text-foreground"
                  >
                    Callback status
                  </Label>
                  <Select
                    value={callbackStatus || '__unset__'}
                    onValueChange={v => setCallbackStatus(normalizeCallbackStatus(v === '__unset__' ? '' : v))}
                  >
                    <SelectTrigger id={`sales-callback-status-${featureIdentityKey ?? 'unknown'}`} className="h-8 text-sm" />
                    <SelectContent>
                      <SelectItem value="__unset__">Not set</SelectItem>
                      <SelectItem value="none">No callback pending</SelectItem>
                      <SelectItem value="requested">Callback requested</SelectItem>
                      <SelectItem value="completed">Callback completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-border/70 bg-background px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Interaction details</div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-contact-outcome-${featureIdentityKey ?? 'unknown'}`}
                    className="text-xs font-medium text-foreground"
                  >
                    Contact outcome
                  </Label>
                  <Select
                    value={contactOutcome || '__unset__'}
                    onValueChange={v => setContactOutcome(normalizeContactOutcome(v === '__unset__' ? '' : v))}
                  >
                    <SelectTrigger id={`sales-contact-outcome-${featureIdentityKey ?? 'unknown'}`} className="h-8 text-sm" />
                    <SelectContent>
                      <SelectItem value="__unset__">Not set</SelectItem>
                      <SelectItem value="resident_contacted">Resident contacted</SelectItem>
                      <SelectItem value="no_answer">No answer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`sales-left-note-${featureIdentityKey ?? 'unknown'}`} className="text-sm text-foreground">
                    Left note
                  </Label>
                  <Switch
                    id={`sales-left-note-${featureIdentityKey ?? 'unknown'}`}
                    checked={leftNote}
                    onCheckedChange={setLeftNote}
                  />
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-best-time-${featureIdentityKey ?? 'unknown'}`}
                    className="text-xs font-medium text-foreground"
                  >
                    Best time to visit
                  </Label>
                  <Input
                    id={`sales-best-time-${featureIdentityKey ?? 'unknown'}`}
                    type="text"
                    value={bestTimeToVisit}
                    onChange={event => setBestTimeToVisit(event.target.value)}
                    placeholder="Morning, after 5pm, weekends…"
                    className="h-8 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label
                    htmlFor={`sales-note-follow-up-${featureIdentityKey ?? 'unknown'}`}
                    className="text-xs font-medium text-foreground"
                  >
                    Suggested follow-up date
                  </Label>
                  <Input
                    id={`sales-note-follow-up-${featureIdentityKey ?? 'unknown'}`}
                    type="date"
                    value={observationFollowUpDate}
                    onChange={event => setObservationFollowUpDate(event.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`sales-note-${featureIdentityKey ?? 'unknown'}`} className="text-xs font-medium text-foreground">
                Note text
              </Label>
              <Textarea
                id={`sales-note-${featureIdentityKey ?? 'unknown'}`}
                value={draft}
                onChange={event => setDraft(event.target.value)}
                rows={3}
                placeholder="Add operator context for this location…"
                className="min-h-[5.5rem] text-sm"
              />
            </div>

            {error ? (
              <div className="rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive">
                <div>{error}</div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-auto px-0 py-0 text-xs text-destructive hover:bg-transparent hover:text-destructive/80"
                  onClick={() => void refresh()}
                >
                  Try again
                </Button>
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button size="sm" onClick={() => void handleSubmit()} disabled={saving || !canSave}>
                {saving ? 'Saving…' : 'Save Update'}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

export function renderSalesMapCollaborationSection(
  args: MapWorkbenchRuntimeSurfaceSectionRenderArgs,
  salesContext?: SalesWorkspaceContext | null,
) {
  if (!isSalesMapCollaborationSection(args.section)) {
    return null;
  }
  return <SalesMapCollaborationSectionBody {...args} salesContext={salesContext} />;
}
